import { useMemo } from 'react';

import type { FilterItemsFunction } from '../types';

type UseFilteredItemsParams<Item> = {
  items: Item[];
  searchInputValue: string;
  itemToStringLabel: (item: Item) => string;
  customFilterItems?: FilterItemsFunction<Item>;
};

/**
 * Hook for filtering items based on search input.
 * Supports custom filtering logic or defaults to label-based filtering.
 */
export const useFilteredItems = <Item>({
  items,
  searchInputValue,
  itemToStringLabel,
  customFilterItems,
}: UseFilteredItemsParams<Item>): Item[] => {
  return useMemo((): Item[] => {
    if (!searchInputValue) return items;

    if (customFilterItems) {
      return customFilterItems({ searchInputValue, items });
    }

    return items.filter((item) =>
      itemToStringLabel(item)
        .toLowerCase()
        .includes(searchInputValue.toLowerCase())
    );
  }, [customFilterItems, items, itemToStringLabel, searchInputValue]);
};
